import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * TutorChat — the shared conversation surface for the AI Tutor tools.
 *
 * Extracted verbatim in behaviour from the five near-identical copies that
 * lived in ExplainPage / InterviewPage / CodeReviewPage / SkillTestPage /
 * ResumeReviewPage. Same markdown pipeline, same bubble semantics, same
 * roles — only the layout differs.
 *
 * One deliberate change: going full-width would have made assistant answers
 * run to 200+ characters per line, which is genuinely harder to read than the
 * old cramped column. So the CONTAINER is full width and the TEXT is capped
 * at a comfortable measure. Wide screens gain usable space without turning
 * explanations into unreadable ribbons.
 */

export const TutorBadge = ({ label, value, dot = "#5B21B6" }) => (
  <div className="px-3 py-1.5 bg-white border border-[#E9E7F3] rounded-xl flex items-center gap-1.5">
    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: dot }} />
    {label ? (
      <span className="text-[9px] font-black uppercase tracking-[0.12em] text-[#94A3B8]">{label}</span>
    ) : null}
    <span className="text-[9px] font-black uppercase tracking-[0.12em] text-[#5B21B6]">{value}</span>
  </div>
);

export const TutorEmptyState = ({ icon, title, description, children }) => (
  <div className="h-full flex flex-col items-center justify-center text-center px-6 py-16">
    <div className="w-20 h-20 rounded-3xl bg-[#F5F2FF] border border-[#E9E7F3] flex items-center justify-center mb-5">
      {icon}
    </div>
    <p className="text-lg font-black tracking-tight text-[#111827]">{title}</p>
    {description ? (
      <p className="text-sm font-medium text-[#94A3B8] mt-2 max-w-md leading-relaxed">{description}</p>
    ) : null}
    {children ? <div className="mt-6 w-full max-w-2xl">{children}</div> : null}
  </div>
);

const markdownComponents = (isUser) => ({
  p: ({ node, ...props }) => <p {...props} className="mb-3 last:mb-0 font-medium" />,
  ul: ({ node, ...props }) => <ul {...props} className="list-disc pl-5 mb-3 space-y-1" />,
  ol: ({ node, ...props }) => <ol {...props} className="list-decimal pl-5 mb-3 space-y-1" />,
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto my-4 rounded-xl border border-[#E9E7F3]">
      <table {...props} className="w-full text-sm" />
    </div>
  ),
  code: ({ node, inline, ...props }) => (
    <code
      {...props}
      className={
        inline
          ? isUser
            ? "bg-white/20 text-white px-1.5 py-0.5 rounded text-sm font-bold"
            : "bg-purple-100 text-[#5B21B6] px-1.5 py-0.5 rounded text-sm font-bold"
          : "block bg-[#1E1B4B] text-green-400 p-5 rounded-2xl border border-white/10 font-mono text-[13px] leading-relaxed my-4 overflow-x-auto shadow-xl"
      }
    />
  ),
});

export const TutorMessage = ({ role, content, assistantLabel = "TSG Assistant" }) => {
  const isUser = role === "user";
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"} animate-in fade-in slide-in-from-bottom-2 duration-300`}
    >
      <div
        className={`p-5 rounded-[22px] shadow-sm border transition-all ${
          isUser
            ? "bg-[#5B21B6] text-white rounded-tr-md border-[#5B21B6] max-w-[min(680px,88%)]"
            : "bg-white text-[#111827] rounded-tl-md border-[#E9E7F3] max-w-[min(880px,94%)]"
        }`}
      >
        <div className="flex items-center gap-2 mb-2">
          <span
            className={`text-[9px] font-black uppercase tracking-[0.14em] ${
              isUser ? "text-purple-200" : "text-[#94A3B8]"
            }`}
          >
            {isUser ? "You" : assistantLabel}
          </span>
        </div>
        <div
          className={`prose max-w-none prose-p:leading-relaxed prose-sm ${
            isUser
              ? "prose-p:text-white prose-headings:text-white prose-strong:text-white prose-li:text-white"
              : "prose-p:text-gray-700 prose-headings:text-[#5B21B6] prose-strong:text-[#5B21B6] prose-li:text-gray-700"
          }`}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents(isUser)}>
            {content}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export const TutorMessages = ({ chat, chatEndRef, empty, assistantLabel }) => (
  <div className="h-full overflow-y-auto custom-scrollbar px-4 md:px-8 py-6">
    {!chat || chat.length === 0 ? (
      empty
    ) : (
      <div className="max-w-[1180px] mx-auto space-y-5">
        {chat.map((msg, idx) => (
          <TutorMessage key={idx} role={msg.role} content={msg.content} assistantLabel={assistantLabel} />
        ))}
      </div>
    )}
    {/* Deliberately a DIRECT child of the scrolling element. Every page
        autoscrolls via chatEndRef.current.parentElement and measures
        scrollHeight/scrollTop on it to decide whether the reader is already
        at the bottom. Nesting this marker inside the centred wrapper would
        hand that effect a non-scrolling parent, silently defeating the
        "do not yank the view if they scrolled up" check. */}
    <div ref={chatEndRef} />
  </div>
);

export const TutorThinking = ({ label = "TSG is thinking..." }) => (
  <div className="flex items-center gap-2 text-[#5B21B6] text-[10px] font-black uppercase tracking-[0.14em] mb-2 px-1">
    <span className="flex space-x-1">
      <span className="w-1.5 h-1.5 bg-[#5B21B6] rounded-full animate-bounce [animation-delay:-0.3s]" />
      <span className="w-1.5 h-1.5 bg-[#5B21B6] rounded-full animate-bounce [animation-delay:-0.15s]" />
      <span className="w-1.5 h-1.5 bg-[#5B21B6] rounded-full animate-bounce" />
    </span>
    {label}
  </div>
);

export const TutorComposer = ({
  value,
  onChange,
  onSubmit,
  loading,
  placeholder = "Ask me anything...",
  disabled = false,
  leading = null,
  multiline = false,
  hint = null,
}) => {
  const canSend = !loading && !disabled;

  const handleKey = (e) => {
    if (multiline) {
      // Enter sends, Shift+Enter makes a new line — the convention people
      // already expect from every other chat surface.
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (canSend) onSubmit();
      }
      return;
    }
    if (e.key === "Enter" && canSend) onSubmit();
  };

  return (
    <div className="max-w-[1180px] mx-auto w-full">
      {loading ? <TutorThinking /> : null}
      <div
        className={`flex items-end gap-2 p-2 bg-white rounded-[22px] border border-[#E9E7F3] shadow-[0_10px_30px_rgba(91,33,182,0.06)] focus-within:ring-4 focus-within:ring-purple-50 focus-within:border-[#C4B5FD] transition-all ${
          multiline ? "" : "items-center"
        }`}
      >
        {leading}
        {multiline ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKey}
            placeholder={placeholder}
            rows={3}
            className="flex-1 bg-transparent px-4 py-3 text-sm font-medium text-[#111827] placeholder-gray-400 focus:outline-none resize-y min-h-[76px] max-h-[320px] font-mono"
          />
        ) : (
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKey}
            placeholder={placeholder}
            className="flex-1 bg-transparent px-5 py-3.5 text-sm font-bold text-[#111827] placeholder-gray-400 focus:outline-none"
          />
        )}
        <button
          onClick={onSubmit}
          disabled={loading || disabled}
          className="w-12 h-12 shrink-0 rounded-2xl bg-gradient-to-tr from-[#5B21B6] to-[#7C3AED] text-white flex items-center justify-center hover:scale-105 active:scale-95 transition-all disabled:opacity-30 disabled:hover:scale-100 shadow-lg shadow-purple-200"
        >
          <svg
            className={`w-5 h-5 ${loading ? "animate-spin" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        </button>
      </div>
      {hint ? (
        <p className="mt-2 text-center text-[10px] font-bold text-[#CBD5E1] uppercase tracking-[0.12em]">
          {hint}
        </p>
      ) : null}
    </div>
  );
};

export default TutorMessages;
